import { useEffect, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

// Speaker button for the homepage song clip.
//
// Desktop (`armOnFirstGesture`): visible from the start as a crossed-out
// speaker. Browsers won't play sound until the visitor interacts, so the
// first click/tap/key anywhere on the page (or on this button) starts the
// song and the icon switches to "on". After that the button mutes/unmutes.
//
// Mobile: the tap that opens the Portal starts the song, so the button only
// appears once it's playing.
export const SoundToggle = ({ audioRef, armOnFirstGesture = false, alwaysVisible = false, className = "" }) => {
  const [on, setOn] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const sync = () => {
      setOn(!a.paused && !a.muted);
      if (!a.paused) setStarted(true);
    };
    const events = ["play", "pause", "ended", "volumechange"];
    events.forEach((e) => a.addEventListener(e, sync));
    return () => events.forEach((e) => a.removeEventListener(e, sync));
  }, [audioRef]);

  useEffect(() => {
    if (!armOnFirstGesture) return;
    const events = ["pointerdown", "keydown", "touchend"];
    const go = (e) => {
      // A press on the button itself is handled by its own onClick.
      if (e.target.closest && e.target.closest("[data-testid=sound-toggle]")) return;
      const a = audioRef.current;
      events.forEach((ev) => window.removeEventListener(ev, go));
      if (a && a.paused && !a.ended) a.play().catch(() => {});
    };
    events.forEach((ev) => window.addEventListener(ev, go));
    return () => events.forEach((ev) => window.removeEventListener(ev, go));
  }, [armOnFirstGesture, audioRef]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      if (a.ended) a.currentTime = 0;
      a.muted = false;
      a.play().catch(() => {});
    } else {
      a.muted = !a.muted;
    }
  };

  const visible = alwaysVisible || started;

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={on ? "Mute music" : "Play music"}
      data-testid="sound-toggle"
      className={`fixed z-50 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white/80 backdrop-blur-md transition-all duration-500 hover:border-[var(--sm-gold)]/60 hover:text-[var(--sm-gold-light)] ${className}`}
      style={{ opacity: visible ? 1 : 0, pointerEvents: visible ? "auto" : "none" }}
    >
      {on ? <Volume2 size={16} /> : <VolumeX size={16} />}
    </button>
  );
};
